import { useEffect, useState } from "react";

import { PaymentScheduleOptions } from "@/components/Payments/AutoPayModal/SchedulePayModal";
import { useSavedPaymentMethods } from "@/hooks/useSavedPaymentMethod";
import { AddNewPaymentMethod, PaymentMethod } from "@/types/PaymentMethod";

// Shared interface between AutoPay + ScheduledPayment
export interface ExistingScheduledPayment {
  paymentMethodId: string;
  specificDate?: string | number;
  paymentScheduleId?: string;
  invoiceId?: string;
  scheduledPaymentDate?: string;
  paymentScheduleOption?: PaymentScheduleOptions;
  soldToOrgId?: string;
}

export type PaymentSelection =
  | PaymentMethod
  | typeof AddNewPaymentMethod
  | undefined;

export function useScheduledPaymentSetup(
  existingSetup?: ExistingScheduledPayment | null
) {
  // Load saved payment methods
  const {
    allPaymentMethods,
    defaultPaymentMethod,
    loading: loadingPaymentMethods,
    error: paymentMethodError
  } = useSavedPaymentMethods();

  const [selectedPayment, setSelectedPayment] =
    useState<PaymentSelection>(undefined);
  const [scheduleOption, setScheduleOption] = useState<PaymentScheduleOptions>(
    existingSetup?.paymentScheduleOption ?? PaymentScheduleOptions.OnDueDate
  );
  const [specificDate, setSpecificDate] = useState<string | number | undefined>(
    existingSetup?.specificDate
  );

  // Preselect the saved payment method
  useEffect(() => {
    if (allPaymentMethods.length === 0) return;

    const existingId = existingSetup?.paymentMethodId;

    if (existingId) {
      const match = allPaymentMethods.find(
        (pm) => pm.paymentMethodId === existingId
      );
      if (match) {
        setSelectedPayment(match);
        return;
      }
    } else if (defaultPaymentMethod) {
      setSelectedPayment(defaultPaymentMethod);
    }
  }, [existingSetup, allPaymentMethods, defaultPaymentMethod]);

  const showAddPaymentMethod = selectedPayment === AddNewPaymentMethod;

  return {
    selectedPayment,
    setSelectedPayment,
    showAddPaymentMethod,

    scheduleOption,
    setScheduleOption,

    specificDate,
    setSpecificDate,

    paymentMethods: {
      all: allPaymentMethods,
      default: defaultPaymentMethod,
      loading: loadingPaymentMethods,
      error: paymentMethodError
    }
  };
}
